/**
 * The start-up translation load (kz2 M12, R11 D). `@ngstack/translate`'s `load()` fetches the fallback
 * language, THEN the active one, each file of each root one after the other — 3 GETs in a row for an
 * English browser, 6 for a French one. This fetches every file at once and merges them in the order
 * ngstack would have (fallback before active; `translationRoot` before each `translatePaths` entry, so
 * the app's keys win), through ngstack's own `use(lang, data)` merge. A file that fails is skipped, as
 * ngstack skips it. The files stay `no-cache` in `firebase.json`: a deploy must reach the next start.
 */
export interface TranslationTarget {
  readonly fallbackLang: string;
  readonly activeLang: string;
  readonly translationRoot: string;
  readonly translatePaths: string[];
  readonly supportedLangs: string[];
  use(lang: string, data?: unknown): Promise<unknown>;
}

/** The languages the start-up needs: the fallback, and the active one when it may have files. */
export function startupLanguages(t: Pick<TranslationTarget, 'fallbackLang' | 'activeLang' | 'supportedLangs'>): string[] {
  const langs = [t.fallbackLang || 'en'];
  const active = t.activeLang;
  const supported = !t.supportedLangs?.length || t.supportedLangs.includes(active);
  if (active && supported && !langs.includes(active)) {
    langs.push(active);
  }
  return langs;
}

/** Every root, once, in ngstack's order. */
export function translationRoots(t: Pick<TranslationTarget, 'translationRoot' | 'translatePaths'>): string[] {
  return [...new Set([t.translationRoot || 'assets/i18n', ...(t.translatePaths ?? [])])];
}

export async function loadTranslationsInParallel(
  t: TranslationTarget,
  fetchJson: (url: string) => Promise<unknown>,
): Promise<void> {
  const langs = startupLanguages(t);
  const roots = translationRoots(t);
  const files = langs.flatMap((lang) => roots.map((root) => ({ lang, url: `${root}/${lang}.json` })));
  const bodies = await Promise.all(files.map((f) => fetchJson(f.url).catch(() => null)));
  for (let i = 0; i < files.length; i++) {
    const body = bodies[i];
    if (body && typeof body === 'object') {
      await t.use(files[i].lang, body);
    }
  }
}
